import { useMemo } from 'react';
import { useDailySunlightStore } from '@/store/dailySunlightsStore';
import { DailySunlightResult } from '@/types/sunlight';
import styles from './styles.module.scss';
import { METRICS_CONFIG } from '@/utils/visualization';

interface SunlightBarChartProps {
	visible: boolean;
}

export const SunlightBarChart = ({ visible }: SunlightBarChartProps) => {
	const { currentSelectedMetric } = useDailySunlightStore();

	const maxValue = useMemo(() => {
		const config = METRICS_CONFIG[currentSelectedMetric] || { max: 10 };
		return config.max;
	}, [currentSelectedMetric]);

	const isInverse = useMemo(() => {
		const config = METRICS_CONFIG[currentSelectedMetric];
		return config?.inverse || false;
	}, [currentSelectedMetric]);

	const getMetricLabel = (metric: keyof DailySunlightResult): string => {
		const labels: Partial<Record<keyof DailySunlightResult, string>> = {
			averageDailySunlightHours: 'hrs',
			consistencyScore: 'score',
			daylightAutonomyPercent: '%',
			winterMonthsAverage: 'hrs',
			springMonthsAverage: 'hrs',
			summerMonthsAverage: 'hrs',
			fallMonthsAverage: 'hrs',
			morningHoursPercent: '%',
			afternoonHoursPercent: '%',
			midDayHoursAverage: 'hrs',
			directionalBalance: 'score',
			glareRiskHours: 'hrs',
			longestSunlightHours: 'hrs',
			shortestSunlightHours: 'hrs',
			minViableDaysCount: 'days',
			daysWithSunlight: 'days',
			zeroDaysCount: 'days',
		};
		return labels[metric] || 'value';
	};

	const unit = getMetricLabel(currentSelectedMetric);

	// Calculate appropriate tick interval based on maxValue
	const getTickInterval = (max: number): number => {
		if (max <= 10) return 1;
		if (max <= 20) return 2;
		if (max <= 50) return 5;
		if (max <= 100) return 10;
		if (max <= 200) return 20;
		if (max <= 500) return 50;
		return 100;
	};

	const tickInterval = getTickInterval(maxValue);
	const ticks = useMemo(() => {
		const result: number[] = [];
		for (let i = 0; i <= maxValue; i += tickInterval) {
			result.push(i);
		}

		if (result[result.length - 1] !== maxValue) {
			result.push(Math.ceil(maxValue));
		}
		return result;
	}, [maxValue, tickInterval]);

	if (!visible) {
		return null;
	}

	let gradientStyle;
	if (isInverse) {
		gradientStyle = {
			background:
				'linear-gradient(to right, #00ff00 0%, #33ff00 10%, #66ff00 20%, #99ff00 30%, #ccff00 40%, #ffff00 50%, #ffcc00 60%, #ff9900 70%, #ff6600 80%, #ff3300 90%, #ff0000 100%)',
		};
	} else {
		gradientStyle = {
			background:
				'linear-gradient(to right, #ff0000 0%, #ff3300 10%, #ff6600 20%, #ff9900 30%, #ffcc00 40%, #ffff00 50%, #ccff00 60%, #99ff00 70%, #66ff00 80%, #33ff00 90%, #00ff00 100%)',
		};
	}

	return (
		<div className={styles.chartContainer}>
			<div className={styles.gradientScale}>
				<div className={styles.gradientBar} style={gradientStyle} />
				<div className={styles.scaleMarkers}>
					{ticks.map((tickValue) => (
						<div
							key={tickValue}
							className={styles.marker}
							style={{ left: `${(tickValue / maxValue) * 100}%` }}
						>
							<div className={styles.tick} />
							<span className={styles.label}>{tickValue}</span>
						</div>
					))}
				</div>
				<div className={styles.unit}>{unit}</div>
			</div>
		</div>
	);
};
